import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Search, Filter, Ticket, AlertTriangle, Clock, CheckCircle2, X, RefreshCw, MessageSquare, Lock, ChevronLeft, Send, UserPlus, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';

const PRI_STYLES = {
  low:      'bg-slate-100  text-slate-700  border-slate-200',
  medium:   'bg-blue-50    text-blue-700   border-blue-200',
  high:     'bg-amber-50   text-amber-700  border-amber-200',
  critical: 'bg-rose-50    text-rose-700   border-rose-200',
};
const STATUS_STYLES = {
  open:                  'bg-amber-50   text-amber-700  border-amber-200',
  assigned:              'bg-indigo-50  text-indigo-700 border-indigo-200',
  in_progress:           'bg-blue-50    text-blue-700   border-blue-200',
  waiting_for_employee:  'bg-purple-50  text-purple-700 border-purple-200',
  waiting_for_vendor:    'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  resolved:              'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed:                'bg-slate-100  text-slate-600   border-slate-200',
  reopened:              'bg-rose-50    text-rose-700   border-rose-200',
};

export default function Tickets() {
  const { id } = useParams();
  if (id) return <TicketDetail tid={id} />;
  return <TicketList />;
}

function TicketList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isStaff = ['admin', 'hr', 'it_admin', 'payroll_manager'].some(r => (user?.roles || []).includes(r) || user?.role === r);
  const isAdmin = user?.role === 'admin' || (user?.roles || []).includes('admin');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [scope, setScope] = useState(isStaff ? 'queue' : 'mine'); // queue|mine|all
  const [createOpen, setCreateOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState(new Set());

  const toggleSel = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const bulkClose = async () => {
    if (selected.size === 0) return;
    const note = window.prompt(`Bulk-close ${selected.size} ticket(s)? Optional note (visible in conversation):`);
    if (note === null) return;
    try {
      const { data } = await api.post('/tickets/bulk-close', { ticket_ids: Array.from(selected), note: note || undefined });
      toast.success(`Closed ${data.summary.closed} · Skipped ${data.summary.skipped}`);
      setSelected(new Set());
      setReload(r => r + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk close failed'); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (statusFilter !== 'all') params.append('status', statusFilter);
        if (priorityFilter !== 'all') params.append('priority', priorityFilter);
        if (scope === 'mine') params.append('mine_only', 'true');
        if (scope === 'queue' && isStaff) params.append('assigned_to_me', 'true');
        const [list, sum] = await Promise.all([
          api.get(`/tickets?${params.toString()}`),
          api.get('/tickets/summary'),
        ]);
        if (cancelled) return;
        setRows(list.data || []);
        setSummary(sum.data);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [statusFilter, priorityFilter, scope, reload, isStaff]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter(r => (r.ticket_no || '').toLowerCase().includes(s)
                           || (r.title || '').toLowerCase().includes(s)
                           || (r.employee_name || '').toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Helpdesk</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Tickets</h1>
          <p className="text-slate-500 mt-1">Raise issues, track resolution, hold SLAs. The HRMS helpdesk.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && selected.size > 0 && (
            <Button onClick={bulkClose} variant="outline" className="rounded-full h-10 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="bulk-close-btn">
              <X className="w-4 h-4 mr-1.5" /> Bulk-close ({selected.size})
            </Button>
          )}
          {isStaff && (
            <a href={(() => {
                const token = localStorage.getItem('hrms_token') || '';
                const base = process.env.REACT_APP_BACKEND_URL || '';
                return `${base}/api/tickets/reports/sla_compliance?format=pdf&auth=${encodeURIComponent(token)}`;
              })()}
              download={`tickets-sla-${new Date().toISOString().slice(0,10)}.pdf`}
              className="inline-flex items-center justify-center rounded-full h-10 px-4 text-sm font-medium border border-slate-200 hover:bg-slate-50"
              data-testid="tickets-export-sla-pdf">
              <Filter className="w-4 h-4 mr-2" /> SLA report (PDF)
            </a>
          )}
          <Button onClick={() => setCreateOpen(true)} className="rounded-full h-10 bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="create-ticket-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New ticket
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="tickets-kpi-strip">
          <Kpi label="Open" value={summary.open} icon={Ticket} accent="text-indigo-600 bg-indigo-50" testid="kpi-open"/>
          <Kpi label="Critical open" value={summary.critical_open} icon={AlertTriangle} accent="text-rose-600 bg-rose-50" testid="kpi-critical"/>
          <Kpi label="Breached SLA" value={summary.breached_open} icon={Clock} accent="text-amber-600 bg-amber-50" testid="kpi-breached"/>
          <Kpi label="Resolved today" value={summary.resolved_today} icon={CheckCircle2} accent="text-emerald-600 bg-emerald-50" testid="kpi-resolved-today"/>
          <Kpi label="Closed (all-time)" value={summary.closed} icon={CheckCircle2} accent="text-slate-600 bg-slate-100" testid="kpi-closed"/>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ticket #, title, requester"
                 className="pl-9 rounded-xl h-10" data-testid="ticket-search"/>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 rounded-xl h-10" data-testid="filter-status">
            <Filter className="w-3.5 h-3.5 mr-1" /><SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['open','assigned','in_progress','waiting_for_employee','waiting_for_vendor','resolved','closed','reopened'].map(s =>
              <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40 rounded-xl h-10" data-testid="filter-priority"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {['low','medium','high','critical'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        {isStaff && (
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-44 rounded-xl h-10" data-testid="filter-scope"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="queue">Assigned to me</SelectItem>
              <SelectItem value="all">My queue + visible</SelectItem>
              <SelectItem value="mine">Tickets I raised</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              {isAdmin && <th className="w-10 px-3 py-3"></th>}
              <th className="text-left px-4 py-3 font-semibold">Ticket #</th>
              <th className="text-left px-4 py-3 font-semibold">Title / Requester</th>
              <th className="text-left px-4 py-3 font-semibold">Category</th>
              <th className="text-left px-4 py-3 font-semibold">Priority</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">SLA</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => {
              const slaBadge = t.sla_breached
                ? <span className="text-rose-700 font-bold">Breached</span>
                : (t.sla_remaining_hours != null
                    ? <span className="text-slate-700">{Math.max(0, Math.round(t.sla_remaining_hours))}h left</span>
                    : '—');
              return (
                <tr key={t.id} onClick={(e) => {
                       // Don't navigate if user clicked the checkbox
                       if (e.target?.tagName === 'INPUT') return;
                       nav(`/tickets/${t.id}`);
                     }}
                     className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`ticket-row-${t.id}`}>
                  {isAdmin && (
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSel(t.id)}
                             className="w-4 h-4 cursor-pointer"
                             data-testid={`sel-${t.id}`} />
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-xs text-[#3D4A8E] font-semibold">{t.ticket_no}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900 truncate max-w-xs">{t.title}</div>
                    <div className="text-[11px] text-slate-500">by {t.employee_name}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.category_name}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${PRI_STYLES[t.priority]}`}>{t.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[t.status]}`}>
                      {(t.status || '').replace(/_/g,' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">{slaBadge}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="text-center text-slate-400 py-12 text-sm">No tickets.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateTicketDialog key={`new-${createOpen}`} open={createOpen} onClose={() => setCreateOpen(false)}
                          onDone={() => { setCreateOpen(false); setReload(r => r + 1); }} />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start justify-between" data-testid={testid}>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</div>
      </div>
      <div className={`w-9 h-9 rounded-xl ${accent} flex items-center justify-center`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
    </div>
  );
}

function CreateTicketDialog({ open, onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/tickets/categories');
        if (cancelled) return;
        setCats((data || []).filter(c => c.active));
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const cat = cats.find(c => c.id === categoryId);

  const save = async () => {
    if (!categoryId || !title.trim() || !description.trim()) {
      toast.error('Category, title and description are required'); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/tickets', {
        category_id: categoryId,
        title,
        description,
        priority: priority || undefined,
      });
      toast.success(`Ticket ${data.ticket_no} created`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create ticket'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-ticket-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Raise a ticket</DialogTitle>
          <DialogDescription>The helpdesk will route this to the right team and email you when there is an update.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Category *</label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="ticket-category"><SelectValue placeholder="Pick a category…" /></SelectTrigger>
              <SelectContent>
                {cats.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    <span className="text-xs text-slate-400 ml-2">SLA {c.sla_hours}h · default {c.default_priority}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cat && <div className="text-[11px] text-slate-500 mt-1">{cat.description}</div>}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Title *</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl mt-1" data-testid="ticket-title"
                   placeholder="One-line summary of the issue" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Description *</label>
            <Textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-xl mt-1" data-testid="ticket-description"
                      placeholder="Steps to reproduce, error messages, what you've tried so far…" />
          </div>
          {cat && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Priority (default: {cat.default_priority})</label>
              <Select value={priority || cat.default_priority} onValueChange={setPriority}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="ticket-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {['low','medium','high','critical'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="confirm-create-ticket">
            {busy ? 'Creating…' : 'Create ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ====== Ticket Detail ====== */
function TicketDetail({ tid }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const isStaff = ['admin','hr','it_admin','payroll_manager'].some(r => (user?.roles || []).includes(r) || user?.role === r);
  const [t, setT] = useState(null);
  const [bump, setBump] = useState(0);
  const [commentBody, setCommentBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/tickets/${tid}`);
        if (!cancelled) setT(data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Ticket not found');
      }
    })();
    return () => { cancelled = true; };
  }, [tid, bump]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/tickets/${tid}`, { status });
      toast.success(`Status updated → ${status.replace(/_/g,' ')}`);
      setBump(b => b + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Status update failed'); }
  };

  const sendComment = async () => {
    if (!commentBody.trim()) return;
    try {
      await api.post(`/tickets/${tid}/comments`, { body: commentBody, internal });
      setCommentBody('');
      setBump(b => b + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add comment'); }
  };

  const resolve = async () => {
    const reason = window.prompt('Resolution note (visible to requester):') || '';
    try { await api.post(`/tickets/${tid}/resolve`, { body: reason }); toast.success('Resolved'); setBump(b => b + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const reopen = async () => {
    const reason = window.prompt('Why are you reopening this?') || '';
    try { await api.post(`/tickets/${tid}/reopen`, { body: reason }); toast.success('Reopened'); setBump(b => b + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const closeTicket = async () => {
    try { await api.post(`/tickets/${tid}/close`); toast.success('Closed'); setBump(b => b + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const sendBack = async () => {
    const note = window.prompt(`Send back to ${t.original_assignee_names?.[0]}? Add a note (what was done):`) || '';
    try {
      await api.post(`/tickets/${tid}/assign-back`, { body: note });
      toast.success('Returned to original assignee');
      setBump(b => b + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!t) return <div className="text-slate-500 text-sm p-6">Loading…</div>;

  const isCreator = user?.id === t.employee_id;
  const canAct = isStaff || isCreator;

  return (
    <div className="space-y-4 max-w-5xl" data-testid="ticket-detail">
      <button onClick={() => nav('/tickets')} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-tickets">
        <ChevronLeft className="w-3 h-3" /> Back to tickets
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-mono text-xs text-[#3D4A8E] font-semibold">{t.ticket_no}</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{t.title}</h1>
            <div className="text-xs text-slate-500 mt-1">
              by <b>{t.employee_name}</b> · {t.category_name} · {new Date(t.created_at).toLocaleString()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${PRI_STYLES[t.priority]}`}>{t.priority}</span>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${STATUS_STYLES[t.status]}`}>{(t.status||'').replace(/_/g,' ')}</span>
            {t.sla_breached
              ? <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-rose-600 text-white">SLA BREACHED</span>
              : (t.sla_remaining_hours != null && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-slate-100 text-slate-700">{Math.max(0, Math.round(t.sla_remaining_hours))}h left</span>)
            }
          </div>
        </div>

        <p className="mt-4 text-sm text-slate-700 whitespace-pre-line">{t.description}</p>

        {/* Holder summary + Actions */}
        {(t.assignee_names?.length || t.original_assignee_names?.length) && (
          <div className="mt-3 text-xs text-slate-600 inline-flex items-center gap-2 flex-wrap">
            <UserPlus className="w-3.5 h-3.5 text-slate-400" />
            <span>Currently with: <b className="text-slate-900">{(t.assignee_names || []).join(', ') || 'unassigned'}</b></span>
            {t.original_assignee_names?.length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] uppercase tracking-wider font-bold">
                Delegated · originally {t.original_assignee_names.join(', ')}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        {canAct && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {isStaff && t.status === 'open' && (
              <Button size="sm" onClick={() => setStatus('in_progress')} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="set-in-progress">Start working</Button>
            )}
            {isStaff && t.status === 'in_progress' && (
              <>
                <Button size="sm" variant="outline" onClick={() => setStatus('waiting_for_employee')} className="rounded-full" data-testid="set-waiting-emp">Waiting for employee</Button>
                <Button size="sm" variant="outline" onClick={() => setStatus('waiting_for_vendor')} className="rounded-full" data-testid="set-waiting-vendor">Waiting for vendor</Button>
              </>
            )}
            {isStaff && (
              <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)} className="rounded-full" data-testid="assign-ticket-btn">
                <UserPlus className="w-3.5 h-3.5 mr-1" /> Assign / Delegate
              </Button>
            )}
            {isStaff && t.original_assignee_ids?.length > 0 && (
              <Button size="sm" variant="outline" onClick={sendBack} className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="send-back-btn">
                <Undo2 className="w-3.5 h-3.5 mr-1" /> Return to {t.original_assignee_names?.[0]}
              </Button>
            )}
            {isStaff && ['open','assigned','in_progress','waiting_for_employee','waiting_for_vendor','reopened'].includes(t.status) && (
              <Button size="sm" onClick={resolve} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="resolve-ticket">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
              </Button>
            )}
            {(isCreator || isStaff) && ['resolved','closed'].includes(t.status) && (
              <Button size="sm" variant="outline" onClick={reopen} className="rounded-full" data-testid="reopen-ticket">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reopen
              </Button>
            )}
            {(isCreator || isStaff) && t.status === 'resolved' && (
              <Button size="sm" variant="outline" onClick={closeTicket} className="rounded-full" data-testid="close-ticket">
                <X className="w-3.5 h-3.5 mr-1" /> Close
              </Button>
            )}
          </div>
        )}
      </div>

      {/* CSAT for the creator after close — auto-shown when ?csat=N in URL too */}
      {isCreator && ['resolved','closed'].includes(t.status) && (
        <CsatBlock tid={tid} csat={t.csat} onDone={() => setBump(b => b + 1)} />
      )}

      {/* Comments */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Conversation</div>
        <div className="space-y-3" data-testid="ticket-comments">
          {(t.comments || []).map(c => (
            <div key={c.id} className={`border rounded-xl p-3 ${c.internal ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`} data-testid={`comment-${c.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="text-xs font-semibold text-slate-900">{c.by_name}</div>
                {c.internal && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 inline-flex items-center gap-1"><Lock className="w-3 h-3"/>Internal note</span>}
                {c.is_resolution && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-900">Resolution</span>}
                <div className="text-[11px] text-slate-400 ml-auto">{new Date(c.at).toLocaleString()}</div>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-line">{c.body}</div>
            </div>
          ))}
          {(!t.comments || t.comments.length === 0) && <div className="text-sm text-slate-400">No messages yet.</div>}
        </div>

        {canAct && t.status !== 'closed' && (
          <div className="mt-4 border-t border-slate-100 pt-4 space-y-2">
            <Textarea rows={3} placeholder="Write a reply…" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} className="rounded-xl" data-testid="comment-input" />
            <div className="flex items-center justify-between flex-wrap gap-2">
              {isStaff && (
                <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="comment-internal-toggle" />
                  <Lock className="w-3 h-3" /> Internal note (hidden from requester)
                </label>
              )}
              <Button onClick={sendComment} disabled={!commentBody.trim()} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E] ml-auto" size="sm" data-testid="send-comment">
                <Send className="w-3.5 h-3.5 mr-1" /> Send
              </Button>
            </div>
          </div>
        )}
      </div>

      <AssignDialog key={`assign-${assignOpen}-${tid}`} open={assignOpen}
                    onClose={() => setAssignOpen(false)}
                    ticket={t}
                    onDone={() => { setAssignOpen(false); setBump(b => b + 1); }} />
    </div>
  );
}

/* Reusable assign / delegate dialog with employee search */
function AssignDialog({ open, onClose, ticket, onDone }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useState({ current: null })[0];

  const runSearch = (val) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/tickets/user-search?q=${encodeURIComponent(val.trim())}`);
        const currentIds = new Set([...(ticket?.assignee_ids || []), ...selected.map(s => s.id)]);
        setResults((data || []).filter(u => !currentIds.has(u.id)));
      } catch (e) { /* ignore */ }
      finally { setSearching(false); }
    }, 250);
  };

  const save = async () => {
    if (selected.length === 0) { toast.error('Pick at least one assignee'); return; }
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/assign`, {
        assignee_ids: selected.map(s => s.id),
        note: note.trim() || undefined,
      });
      toast.success(`Reassigned to ${selected.map(s => s.name).join(', ')}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ticket-assign-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Assign / Delegate</DialogTitle>
          <DialogDescription>
            Currently with <b>{(ticket?.assignee_names || []).join(', ') || 'unassigned'}</b>.
            Search anyone in the company — admin and category-handlers can delegate across departments.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Search by name, email, or Employee ID</label>
            <Input value={q} onChange={(e) => runSearch(e.target.value)} placeholder="e.g. Priya, LAN-2026-…"
                   className="rounded-xl mt-1" data-testid="assign-user-search" />
            {searching && <div className="text-xs text-slate-500 mt-1">Searching…</div>}
            {results.length > 0 && (
              <div className="border border-slate-200 rounded-xl mt-2 max-h-60 overflow-y-auto" data-testid="assign-user-results">
                {results.map(u => (
                  <button key={u.id} type="button"
                          onClick={() => { setSelected(s => [...s, u]); setResults([]); setQ(''); }}
                          className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                          data-testid={`assign-user-${u.id}`}>
                    <div className="font-semibold text-slate-900 text-sm">{u.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {u.employee_id && <span className="font-mono">{u.employee_id}</span>}
                      {u.department && <> · {u.department}</>}
                      {u.primary_role && <> · {u.primary_role.replace('_',' ')}</>}
                      {u.email && <> · {u.email}</>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selected.length > 0 && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-3" data-testid="assign-selected-list">
              <div className="text-xs font-semibold text-emerald-900 mb-2">Will be assigned to:</div>
              <div className="flex flex-wrap gap-1.5">
                {selected.map(s => (
                  <span key={s.id} className="inline-flex items-center gap-1 bg-white border border-emerald-300 rounded-full px-2 py-0.5 text-xs">
                    {s.name}
                    <button onClick={() => setSelected(arr => arr.filter(x => x.id !== s.id))} className="text-slate-400 hover:text-rose-600" data-testid={`unselect-${s.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-600">Delegation note (visible in conversation)</label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl mt-1"
                      placeholder="What you need them to do; they can hand it back to you when done."
                      data-testid="assign-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy || selected.length === 0} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="confirm-assign-ticket">
            {busy ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------- CSAT survey block (creator-only, after resolved/closed) ---------- */
function CsatBlock({ tid, csat, onDone }) {
  const [rating, setRating] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const r = parseInt(params.get('csat') || '0', 10);
      return (r >= 1 && r <= 5) ? r : 0;
    } catch { return 0; }
  });
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (rating < 1 || rating > 5) { toast.error('Pick a star rating first'); return; }
    setBusy(true);
    try {
      await api.post(`/tickets/${tid}/csat`, { rating, feedback });
      toast.success('Thanks for your feedback!');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit'); }
    finally { setBusy(false); }
  };

  if (csat?.rating) {
    return (
      <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6" data-testid="csat-submitted">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Your feedback</div>
        <div className="font-heading text-lg font-semibold text-slate-900 mt-1">
          You rated this <span className="text-emerald-700">{csat.rating} / 5</span>
        </div>
        {csat.feedback && <div className="text-sm text-slate-700 mt-2 italic">&ldquo;{csat.feedback}&rdquo;</div>}
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6" data-testid="csat-prompt">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">How did we do?</div>
      <div className="font-heading text-lg font-semibold text-slate-900 mt-1">Rate your experience with this ticket</div>
      <div className="flex items-center gap-1 mt-3" data-testid="csat-stars">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button"
                  onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)} data-testid={`csat-star-${n}`}
                  className={`w-9 h-9 rounded-full text-xl transition-all ${(hover || rating) >= n ? 'text-amber-500' : 'text-slate-300'}`}>
            ★
          </button>
        ))}
        <span className="ml-3 text-sm text-slate-600">
          {rating > 0 ? ['Very poor', 'Poor', 'OK', 'Good', 'Excellent'][rating - 1] : 'Tap a star'}
        </span>
      </div>
      <Textarea rows={2} placeholder="Anything we could do better? (optional)" value={feedback}
                onChange={(e) => setFeedback(e.target.value)} className="rounded-xl mt-3"
                data-testid="csat-feedback" />
      <div className="mt-3 flex justify-end">
        <Button onClick={submit} disabled={busy || rating < 1}
                className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="csat-submit">
          {busy ? 'Submitting…' : 'Submit feedback'}
        </Button>
      </div>
    </div>
  );
}
